import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../Config/config";
import { useTenant } from "../Context/TenantContext";
import CollegeHeader from "./CollegeHeader";
import CollegeHomeSideBar from "./CollegeHomeSideBar";
import CollegeFooter from "./CollegeFooter";
import { useNavigate } from "react-router-dom";
import { parseLogoUrl, getFallbackLogo } from "../utils/logoUtils";
import { AlertCircle, Building2, Pencil, MapPin, Mail, Phone, Globe, User, Images } from "lucide-react";

// --- Design Tokens ------------------------------------------------------------

const T = {
  border:  "1px solid var(--border-subtle, #E2E8F0)",
  radius:  { sm: "8px", md: "12px", lg: "16px", xl: "20px", xxl: "24px", full: "999px" },
  shadow:  { xs: "0 1px 3px rgba(0,0,0,0.05)", sm: "0 2px 8px rgba(0,0,0,0.07)", md: "0 4px 20px rgba(0,0,0,0.08)" },
  brand:   "var(--brand-primary, var(--primary-blue, #4F46E5))",
  text: {
    heading:  "var(--text-heading,  #0F172A)",
    body:     "var(--text-body,     #334155)",
    muted:    "var(--text-muted,    #64748B)",
    disabled: "var(--text-disabled, #94A3B8)",
  },
  bg: { main: "var(--bg-main, #F8FAFC)", section: "var(--bg-section, #FFFFFF)" },
};

// --- ProfileField -------------------------------------------------------------

const ProfileField = ({ icon: Icon, label, value, isLink }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", gap: "14px",
    padding: "16px 18px", borderRadius: T.radius.lg,
    background: T.bg.main, border: T.border,
  }}>
    <div style={{
      width: "36px", height: "36px", borderRadius: T.radius.md,
      background: "rgba(79, 70, 229,0.08)", border: "1px solid rgba(79, 70, 229,0.14)",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <Icon style={{ width: "16px", height: "16px", color: T.brand }} />
    </div>
    <div style={{ minWidth: 0 }}>
      <p style={{
        fontSize: "10px", fontWeight: 700, color: T.text.muted,
        textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 4px",
      }}>
        {label}
      </p>
      {isLink ? (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          style={{
            fontSize: "14px", fontWeight: 600, color: T.brand,
            textDecoration: "none", wordBreak: "break-all",
          }}
        >
          {value || "-"}
        </a>
      ) : (
        <p style={{
          fontSize: "14px", fontWeight: 600, color: T.text.heading,
          margin: 0, wordBreak: "break-word",
        }}>
          {value || "-"}
        </p>
      )}
    </div>
  </div>
);

// --- CollegeProfile -----------------------------------------------------------

const CollegeProfile = () => {
  const [college, setCollege] = useState(null);
  const [error,   setError]   = useState("");
  const { tenant, getLogoUrl } = useTenant();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.username) { setError("User not found"); return; }
        const res = await axios.get(`${API_BASE_URL}/colleges/username/${user.username}`);
        setCollege(res.data);
      } catch (err) {
        console.error("❌ Failed to load college profile:", err);
        setError("Failed to load college profile");
      }
    };
    fetchProfile();
  }, []);

  // -- Loading ------------------------------------------------------------------
  if (!college && !error) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg.main, display: "flex", flexDirection: "column" }}>
        <CollegeHeader />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
          <div style={{ width: "20px", height: "20px", border: `2px solid ${T.brand}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <p style={{ fontSize: "14px", color: T.text.muted, fontWeight: 500 }}>Loading profile...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // -- Error --------------------------------------------------------------------
  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg.main, display: "flex", flexDirection: "column" }}>
        <CollegeHeader />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ textAlign: "center", maxWidth: "360px" }}>
            <div style={{ width: "52px", height: "52px", borderRadius: T.radius.lg, background: "#FEF2F2", border: "1px solid #FECACA", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <AlertCircle style={{ width: "22px", height: "22px", color: "#DC2626" }} />
            </div>
            <p style={{ fontWeight: 700, color: T.text.heading, fontSize: "15px", margin: "0 0 6px" }}>Failed to load profile</p>
            <p style={{ fontSize: "13px", color: T.text.muted, margin: 0 }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // -- Logo resolution ----------------------------------------------------------
  const rawLogo = college.logo || tenant?.logoUrl || tenant?.logo_url || null;
  const logoPath = parseLogoUrl(rawLogo) || getLogoUrl() || getFallbackLogo();

  // -- Campus images ------------------------------------------------------------
  const parseCampusImages = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(img => img && typeof img === 'string');
    if (typeof raw === 'string') {
      const s = raw.trim();
      if (s.startsWith('{') && s.endsWith('}')) {
        return s.slice(1, -1).split(',').map(u => u.trim().replace(/^"|"$/g, '')).filter(Boolean);
      }
      try { const p = JSON.parse(s); return Array.isArray(p) ? p.filter(img => img && typeof img === 'string') : []; } catch {}
    }
    return [];
  };
  const campusImages = parseCampusImages(college.images);

  const { name, state, country, email, phone, website, username } = college;

  return (
    <div className="college-main-layout" style={{ background: T.bg.main, fontFamily: "inherit" }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content">
          <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "36px 28px 72px" }}>

            {/* -- Page Header --------------------------------------------- */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: "16px", marginBottom: "32px", flexWrap: "wrap",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{
                  width: "48px", height: "48px", borderRadius: T.radius.lg,
                  background: "rgba(79, 70, 229,0.08)", border: "1px solid rgba(79, 70, 229,0.15)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <Building2 style={{ width: "22px", height: "22px", color: T.brand }} />
                </div>
                <div>
                  <h1 style={{ fontSize: "22px", fontWeight: 800, margin: 0, color: T.text.heading, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                    Institution Profile
                  </h1>
                  <p style={{ fontSize: "13px", color: T.text.muted, margin: "4px 0 0", fontWeight: 500 }}>
                    View your college's registered information
                  </p>
                </div>
              </div>

              <button
                onClick={() => navigate("/CollegeEditProfile")}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "7px",
                  padding: "9px 20px", borderRadius: T.radius.md,
                  background: T.brand, color: "#FFFFFF",
                  border: "none", cursor: "pointer",
                  fontSize: "13px", fontWeight: 700,
                  boxShadow: "0 3px 12px rgba(79, 70, 229,0.28)",
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.88"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              >
                <Pencil style={{ width: "14px", height: "14px" }} />
                Edit Profile
              </button>
            </div>

            {/* -- Identity Card ------------------------------------------- */}
            <div style={{
              background: "#FFFFFF", border: T.border, borderRadius: T.radius.xxl,
              overflow: "hidden", boxShadow: T.shadow.md, marginBottom: "20px",
            }}>
              <div style={{ padding: "32px 36px", display: "flex", alignItems: "center", gap: "32px", flexWrap: "wrap" }}>
                {/* Logo */}
                <div style={{ flexShrink: 0 }}>
                  <div style={{
                    width: "100px", height: "100px", borderRadius: T.radius.xl,
                    background: "#F8FAFC", border: "2px solid rgba(79, 70, 229,0.15)",
                    overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: T.shadow.sm,
                  }}>
                    <img
                      src={logoPath}
                      alt="College Logo"
                      style={{ width: "100%", height: "100%", objectFit: "contain", padding: "8px" }}
                      onError={(e) => { if (e.target.src !== window.location.origin + '/logo192.png') e.target.src = '/logo192.png'; }}
                    />
                  </div>
                </div>

                {/* Identity info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: "6px",
                    padding: "3px 12px", borderRadius: T.radius.full,
                    background: "rgba(79, 70, 229,0.06)", border: "1px solid rgba(79, 70, 229,0.15)",
                    fontSize: "10px", fontWeight: 700, color: T.brand,
                    textTransform: "uppercase", letterSpacing: "0.06em",
                    marginBottom: "10px",
                  }}>
                    🎓 Verified Institution
                  </div>
                  <h2 style={{
                    fontSize: "clamp(1.2rem, 2.5vw, 1.6rem)",
                    fontWeight: 800, color: T.text.heading,
                    letterSpacing: "-0.02em", lineHeight: 1.2, margin: "0 0 8px",
                  }}>
                    {name || "-"}
                  </h2>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <MapPin style={{ width: "13px", height: "13px", color: T.text.muted }} />
                    <span style={{ fontSize: "13px", color: T.text.muted, fontWeight: 500 }}>
                      {[state, country].filter(Boolean).join(", ") || "Location not set"}
                    </span>
                  </div>
                </div>

                {/* Username badge */}
                <div style={{
                  padding: "12px 18px", borderRadius: T.radius.lg,
                  background: T.bg.main, border: T.border,
                  textAlign: "center", flexShrink: 0,
                }}>
                  <p style={{ fontSize: "10px", fontWeight: 700, color: T.text.muted, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 4px" }}>
                    Portal ID
                  </p>
                  <p style={{
                    fontSize: "14px", fontWeight: 800, color: T.text.heading,
                    margin: 0, fontFamily: "monospace", letterSpacing: "0.02em",
                  }}>
                    {username || "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* -- Contact Details Grid ------------------------------------- */}
            <div style={{
              background: "#FFFFFF", border: T.border, borderRadius: T.radius.xl,
              padding: "24px 28px", boxShadow: T.shadow.xs, marginBottom: "20px",
            }}>
              <p style={{
                fontSize: "11px", fontWeight: 700, color: T.text.muted,
                textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 18px",
              }}>
                Contact Information
              </p>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: "12px",
              }}>
                <ProfileField icon={Mail}  label="Email Address" value={email} />
                <ProfileField icon={Phone} label="Phone Number"  value={phone} />
                <ProfileField icon={Globe} label="Website URL"   value={website} isLink />
                <ProfileField icon={User}  label="Admin Username" value={username} />
              </div>
            </div>

            {/* -- Campus Showcase ----------------------------------------- */}
            <div style={{
              background: "#FFFFFF", border: T.border, borderRadius: T.radius.xl,
              padding: "24px 28px", boxShadow: T.shadow.xs,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
                <Images style={{ width: "15px", height: "15px", color: T.brand }} />
                <p style={{
                  fontSize: "11px", fontWeight: 700, color: T.text.muted,
                  textTransform: "uppercase", letterSpacing: "0.06em", margin: 0,
                }}>
                  Campus Showcase
                  {campusImages.length > 0 && (
                    <span style={{
                      marginLeft: "8px", fontSize: "10px", fontWeight: 700,
                      color: T.brand, background: "rgba(79, 70, 229,0.07)",
                      padding: "1px 8px", borderRadius: T.radius.full,
                    }}>
                      {campusImages.length} photo{campusImages.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </p>
              </div>

              {campusImages.length === 0 ? (
                <div style={{
                  padding: "40px 24px", textAlign: "center",
                  background: T.bg.main, borderRadius: T.radius.lg, border: T.border,
                }}>
                  <span style={{ fontSize: "28px", display: "block", marginBottom: "10px", opacity: 0.4 }}>🏛️</span>
                  <p style={{ fontSize: "13px", color: T.text.muted, margin: "0 0 6px", fontWeight: 600 }}>
                    No campus images yet
                  </p>
                  <p style={{ fontSize: "12px", color: T.text.disabled, margin: 0 }}>
                    Add campus photos from the Edit Profile page.
                  </p>
                </div>
              ) : (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: "12px",
                }}>
                  {campusImages.map((img, index) => (
                    <div key={index} style={{
                      borderRadius: T.radius.lg, overflow: "hidden",
                      border: T.border, boxShadow: T.shadow.xs,
                      aspectRatio: "16/10",
                    }}>
                      <img
                        src={img}
                        alt={`Campus ${index + 1}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </main>
      </div>

      <CollegeFooter />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CollegeProfile;

